import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';

import RecordBadge from '../common/RecordBadge';
import DemoTag from '../items/DemoTag';
import SearchSnippet, { hasSnippetText, isTitleSnippet } from './SearchSnippet';
import { highlightParts } from '../../utils/searchHighlight';
import {
	evidenceSentence,
	foundCardPath,
	foundItemPath,
	seeAllPath,
} from '../../utils/collectionSearchPaths';

/** `text` with the words of `term` marked. */
export function Highlighted({ text, term }) {
	return (
		<>
			{highlightParts(text, term).map((part, index) => (
				part.mark
					? <mark key={index} className='search-mark'>{part.text}</mark>
					: <React.Fragment key={index}>{part.text}</React.Fragment>
			))}
		</>
	);
}

const countWords = n => `${n.toLocaleString('en-US')} ${n === 1 ? 'item' : 'items'}`;

/** Why the CARD matched, when it did by its own words. */
function cardMatchNote(match) {
	if (!match) return '';
	const elements = Array.isArray(match.elements) ? match.elements.filter(Boolean) : [];
	if (match.name && elements.length) return `Matches the card’s name and its ${elements.slice(0, 3).join(', ')} element`;
	if (match.name) return 'Matches the card’s name';
	if (elements.length) return `Has an element named ${elements.slice(0, 3).join(', ')}`;
	return '';
}

/**
 * One card's group: the card once, as its badge, what matched in it, and its
 * matching items under it (KPR-355).
 *
 * `count` is the server's, exact for THIS card alone (a parent and its child
 * card are counted apart) and equal to what the list shows for the same card
 * and term. It is null above the server's line (`counted: false`): then no
 * number is shown anywhere, and "See all" says no N.
 */
export function CardGroup({ card, term, collectionId }) {
	const count = Number.isFinite(card.count) ? card.count : null;
	const items = Array.isArray(card.items) ? card.items : [];
	const note = cardMatchNote(card.match);
	const title = card.name || 'Card';
	const showSeeAll = count === null || count > 0;
	return (
		<section className='collection-search__group' aria-label={title} data-testid='search-group' data-card-id={card.id}>
			<header className='collection-search__group-head'>
				<div className='collection-search__group-id'>
					<RecordBadge
						kind='card'
						record={{ icon: card.icon, color: card.color, name: title }}
						title={<Highlighted text={title} term={term} />}
						size='sm'
						to={foundCardPath(card.id, collectionId)}
					/>
					{note ? <span className='collection-search__note'>{note}</span> : null}
				</div>
				<div className='collection-search__group-actions'>
					{count !== null ? (
						<span className='collection-search__count' data-testid='group-count'>{countWords(count)}</span>
					) : null}
					{showSeeAll ? (
						<Link
							className='collection-search__see-all'
							to={seeAllPath(collectionId, card.id, term)}
						>
							{count === null ? 'See all' : `See all ${count.toLocaleString('en-US')}`}
							<span className='collection-search__sr'>{` ${title} items matching ${term}`}</span>
						</Link>
					) : null}
				</div>
			</header>
			{items.length ? (
				<ul className='collection-search__items'>
					{items.map((item) => {
						const evidence = evidenceSentence(item.matchedIn);
						// The words in context (KPR-490): a few words of one field,
						// the matches marked. A snippet of the title is the line
						// above, so it is not said twice; an answer without one
						// (an older API) shows the title alone.
						const snippet = item.snippet && !isTitleSnippet(item.snippet) && hasSnippetText(item.snippet) ? item.snippet : null;
						return (
							<li key={item.id} className='collection-search__item'>
								<Link className='collection-search__item-link' to={foundItemPath(item, collectionId)}>
									<span className='collection-search__item-title'>
										<Highlighted text={item.displayValue || 'Untitled'} term={term} />
										<DemoTag item={item} />
									</span>
									{snippet ? <SearchSnippet snippet={snippet} className='collection-search__snippet' /> : null}
									{evidence ? <span className='collection-search__evidence'>Matched in {evidence}</span> : null}
								</Link>
							</li>
						);
					})}
				</ul>
			) : (
				<p className='collection-search__no-items'>
					{count === 0 ? 'No items of this card match.' : 'More matches are on the next page.'}
				</p>
			)}
		</section>
	);
}

/** The groups of one answer, and "Load more" while the server has another page. */
export default function CollectionSearchResults({ answer, term, collectionId, onLoadMore, loadingMore }) {
	return (
		<div className='collection-search__results'>
			{answer.cards.map(card => (
				<CardGroup key={card.id} card={card} term={term} collectionId={collectionId} />
			))}
			{answer.cardsCapped ? (
				<p className='collection-search__note'>Only the first 100 cards that match by name are listed. Add a word to narrow them.</p>
			) : null}
			{answer.nextCursor ? (
				<div className='collection-search__more'>
					<Button onClick={onLoadMore} loading={loadingMore}>Load more</Button>
				</div>
			) : null}
		</div>
	);
}
